import React, { useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter as Router, Routes, Route, useParams, Navigate } from 'react-router-dom';
import HomePage from './components/HomePage';
import LoginPage from './components/LoginPage';
import NicknameSetup from './components/NicknameSetup';
import QuizPlayer from './components/QuizPlayer';
import UserProfile from './components/UserProfile';
import PublicProfile from './components/PublicProfile';
import SettingsPage from './components/SettingsPage';
import NotificationsPage from './components/NotificationsPage';
import AchievementsPage from './components/AchievementsPage';
import LeaderboardPage from './components/LeaderboardPage';
import AchievementsCatalogPage from './components/AchievementsCatalogPage';
import InventoryPage from './components/InventoryPage';
import AdminPanel from './components/AdminPanel';
import VipPage from './components/VipPage';
import ErrorPage from './components/ErrorPage';
import PvPQuiz from './components/PvPQuiz';
import AboutPage from './components/AboutPage';
import ContactPage from './components/ContactPage';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';

import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './App.css';
import './components/vip-subscriber.css';
import { Toaster } from 'react-hot-toast';
import I18nWrapper from './components/I18nWrapper';
import AppFooter from './components/AppFooter';
import { HOME_PVP_ARENA_PATH } from './navigationPaths';
import DocumentMeta from './components/DocumentMeta';
import GlobalJsonLd from './components/GlobalJsonLd';
import MetaPixelRouteTracker from './components/MetaPixelRouteTracker';
import { SOLO_QUICK_MATCH_ROUTE_ID } from './services/api';
import { initAppBgmPreference } from './audio/appBgmPreference';
import { initQuizSfxPreference, preloadQuizSfx } from './audio/quizSfx';
import { QuizSoundPreferenceProvider } from './contexts/QuizSoundPreferenceContext';
import { AppBackgroundMusicProvider } from './contexts/AppBackgroundMusicContext';

// Wrapper component to extract quizId from URL params
const QuizPlayerWrapper: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return id ? <QuizPlayer quizId={id} /> : <ErrorPage />;
};

function App() {
  initQuizSfxPreference();
  initAppBgmPreference();
  useEffect(() => {
    preloadQuizSfx();
  }, []);

  return (
    <I18nWrapper>
      <HelmetProvider>
      <QuizSoundPreferenceProvider>
      <Router>
        <AppBackgroundMusicProvider>
        <MetaPixelRouteTracker />
        <DocumentMeta />
        <GlobalJsonLd />
        <div className="App app-layout">
          <Toaster position="top-center" reverseOrder={false} />
          <div className="app-main">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/nickname-setup" element={<NicknameSetup />} />
            <Route path={`/quiz/${SOLO_QUICK_MATCH_ROUTE_ID}`} element={<QuizPlayer quizId={SOLO_QUICK_MATCH_ROUTE_ID} />} />
            <Route path="/quiz/:id" element={<QuizPlayerWrapper />} />
            <Route path="/pvp" element={<Navigate to={HOME_PVP_ARENA_PATH} replace />} />
            <Route path="/pvp-quiz/:roomId" element={<PvPQuiz />} />
            <Route path="/leaderboard" element={<LeaderboardPage />} />
            <Route path="/achievements/catalog" element={<AchievementsCatalogPage />} />
            <Route path="/inventory" element={<InventoryPage />} />
            <Route path="/achievements" element={<AchievementsPage />} />
            <Route path="/profile/:userId/achievements" element={<AchievementsPage />} />
            <Route path="/profile" element={<UserProfile />} />
            <Route path="/profile/:userId" element={<PublicProfile />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/vip" element={<VipPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/admin" element={<AdminPanel />} />
            <Route path="*" element={<ErrorPage />} />
          </Routes>
          </div>
          <AppFooter />
        </div>
        </AppBackgroundMusicProvider>
      </Router>
      </QuizSoundPreferenceProvider>
      </HelmetProvider>
    </I18nWrapper>
  );
}

export default App;
